import React, { useCallback, useContext, useState } from 'react';
import { submissionToUpdateGraphQLInput } from '../../SubmitGame/submission-helpers';
import { toast } from 'react-toastify';
import { updateSubmission } from '../Mutations/update-submission-mutation';
import { SubmissionContext } from '../Context/SubmissionProvider';
import GameDetailsFormLoader from '../GameDetailsForm/GameDetailsFormLoader';
import { useHistory } from 'react-router-dom';
import { getLatestBuild } from '../../../../../../common/build-utils';
import ActionLink, { ACTION_LINK_HEIGHT } from '../../../../Footer/ActionLink';
import { Box } from '@mui/material';
import BackToPreviewConfirmationPopup from '../BackToPreviewConfirmationPopup';

interface DetailsStepProps {
  goToLegal(): void;
}

// Dev can land on this step after QA or after base step if no QA is needed
// Only next step is Legal, you can also go back to Draft
const DetailsStep: React.FC<DetailsStepProps> = ({ goToLegal }) => {
  const {
    baseForm,
    detailsForm,
    detailsFormProblems,
    submission,
    setAttemptedToSave,
    setIsModified,
    modified,
    setBuildId,
    updateSubmission: updateSubmissionInContext,
  } = useContext(SubmissionContext);
  const history = useHistory();

  const [isSaving, setIsSaving] = useState(false);
  const [showBackToPreviewDialog, setShowBackToPreviewDialog] = useState(false);

  const submit = useCallback(async () => {
    if (!detailsFormProblems && !modified) {
      goToLegal();
      return;
    }

    if (detailsFormProblems && detailsFormProblems.length > 0) {
      console.error(`Form problems: ${detailsFormProblems}`);
      toast.error('Please check if all the mandatory fields marked with * are filled before continuing', { autoClose: 8000 });
      setAttemptedToSave(true);
      return;
    }

    setIsSaving(true);

    try {
      const updateInput = submissionToUpdateGraphQLInput(submission!, 'details', baseForm!, detailsForm);
      const res = await updateSubmission(updateInput);
      const { gameBuilds } = res.data!.developerSubmissionUpdate;
      updateSubmissionInContext(res.data!.developerSubmissionUpdate);
      const latestBuild = getLatestBuild(gameBuilds);
      setBuildId(latestBuild.id, () => {
        setIsSaving(false);
        setIsModified(false, () => {
          goToLegal();
        });
      });
    } catch (e) {
      console.error(e);
      const errorMsg = e instanceof Error ? e.message : 'UNKNOWN_ERROR';
      toast.error(errorMsg, { autoClose: 5000 });
      setIsSaving(false);
    }
  }, [
    modified,
    detailsFormProblems,
    goToLegal,
    setAttemptedToSave,
    submission,
    baseForm,
    detailsForm,
    setIsModified,
    setBuildId,
    updateSubmissionInContext,
  ]);

  return (
    <Box sx={{ paddingBottom: `${ACTION_LINK_HEIGHT}px` }}>
      <GameDetailsFormLoader />
      <BackToPreviewConfirmationPopup
        submission={submission!}
        opened={showBackToPreviewDialog}
        onClose={() => {
          setShowBackToPreviewDialog(false);
        }}
      />
      <ActionLink
        primaryButton={{
          title: modified ? 'Save and continue to last step' : 'Continue to last step',
          callback: submit,
          isLoading: isSaving,
        }}
        middleButton={{
          title: 'Preview',
          callback: () => {
            setShowBackToPreviewDialog(true);
          },
        }}
        primaryButtonDisabled={Object.values(detailsForm.videoUploadProgress).some((progress) => progress !== 'not-started')}
        secondaryButton={{
          // leads you back to the file upload section
          title: 'Upload new version',
          callback: () => {
            history.replace({ search: '' }); // remove possible fromQA query param
            window.location.reload();
          },
        }}
        secondaryButtonDisabled={isSaving}
      />
    </Box>
  );
};

export default DetailsStep;
